import React, { useRef, useEffect, useState } from 'react'
// import { Mic, Plus, AudioWaveform, X, Expand, Send, Loader2 } from 'lucide-react'
import { Send, Loader2, Expand } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { MentionList } from '@/components/chat/MentionList'
import { useTheme } from '@/context/ThemeContext'
import { useChatStore } from '../askAnything/chatStore'
import { useCustomChat } from '../askAnything/useCustomChat'
import ExpandedComposeDialog from './ExpandedComposeDialog'
import { TimeWindowSelect } from '@/components/chat/TimeWindowSelect'

type Mention = {
  id: string
  type: string
  name: string
}

interface ChatInputProps {
  currentThreadId: string
  input: string
  isProcessing: boolean
  // isListening: boolean
  // toggleListening: () => void
  // uploadedFiles: File[]
  // handleFileUpload: () => void
  // removeUploadedFile: (index: number) => void
  showMentions: boolean
  filteredMentions: Mention[]
  onMentionSelect: (mention: Mention) => void
  toggleExpandedMode: () => void
  // launchAudioConversation: () => void
}

const ChatInput: React.FC<ChatInputProps> = ({
  currentThreadId,
  input: propInput,
  isProcessing,
  // isListening,
  // toggleListening,
  // uploadedFiles,
  // handleFileUpload,
  // removeUploadedFile,
  showMentions,
  filteredMentions,
  onMentionSelect,
  toggleExpandedMode,
  // launchAudioConversation,
}) => {
  const [showExpandIcon, setShowExpandIcon] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  // const fileInputRef = useRef<HTMLInputElement>(null)
  const { isDarkMode } = useTheme()
  const [input, setInput] = useState(propInput || '')

  const { timeWindow, setTimeWindow } = useChatStore()

  // Update local state when prop changes
  useEffect(() => {
    if (propInput !== undefined) {
      setInput(propInput)
    }
  }, [propInput])

  const { append } = useCustomChat({
    id: currentThreadId,
  })

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`

      setShowExpandIcon(textareaRef.current.scrollHeight > 60)
    }
  }, [input])

  const handleButtonClick = () => {
    if (input.trim()) {
      append({
        role: 'user',
        content: input,
      })
      setInput('')
    }
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleButtonClick()
    }
  }

  return (
    <>
      <div className={`border-t p-4 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="flex items-stretch gap-2">
          <div className="flex-1 relative">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Type your message..."
              className={`min-h-[60px] max-h-[200px] resize-none ${
                isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}
              disabled={isProcessing}
            />
            {showMentions && (
              <div className="absolute bottom-full left-0 mb-2">
                <MentionList mentions={filteredMentions} onSelect={onMentionSelect} />
              </div>
            )}
          </div>

          <div className="flex flex-col justify-between py-1">
            {showExpandIcon && (
              <button
                onClick={() => setIsExpanded(true)}
                className={`p-1.5 ${
                  isDarkMode
                    ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                } rounded ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                title="Expand editor"
                disabled={isProcessing}
              >
                <Expand size={18} />
              </button>
            )}

            <div className="flex items-center gap-2">
              {isProcessing && (
                <div className="flex items-center text-gray-400 mr-2">
                  <Loader2 size={16} className="animate-spin mr-1" />
                  <span className="text-xs">Loading...</span>
                </div>
              )}
              {/* <button
                onClick={toggleListening}
                className={`p-2 rounded-full ${
                  isListening
                    ? 'bg-red-100 text-red-500'
                    : isDarkMode
                      ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={isProcessing}
              >
                <Mic size={18} />
              </button> */}

              <Button
                onClick={handleButtonClick}
                disabled={isProcessing || !input.trim()}
                className={`rounded-full p-3 h-auto ${isProcessing ? 'opacity-50' : ''}`}
              >
                <Send size={18} />
              </Button>
            </div>
          </div>
        </div>

        <div className="flex justify-start mt-2">
          <TimeWindowSelect value={timeWindow} onChange={setTimeWindow} />
        </div>
      </div>

      <ExpandedComposeDialog
        isOpen={isExpanded}
        onClose={() => setIsExpanded(false)}
        input={input}
        onInputChange={setInput}
        onSend={handleButtonClick}
        isProcessing={isProcessing}
      />
    </>
  )
}

export default ChatInput
